export const GUEST_PASS_DURATION_OPTIONS = [
  { value: '1d', label: '1 day' },
  { value: '2d', label: '2 days' },
  { value: '3d', label: '3 days' },
  { value: '4d', label: '4 days' },
  { value: '5d', label: '5 days' },
  { value: '6d', label: '6 days' },
  { value: '7d', label: '7 days' },
];

export const GUEST_PASS_DURATION_VALUES = GUEST_PASS_DURATION_OPTIONS.map((o) => o.value);

/** Spa Sale + Oxybar Sale: 1–7 days only. */
export const SPA_OXYBAR_DURATION_OPTIONS = [
  { value: '1d', label: '1 day' },
  { value: '2d', label: '2 day' },
  { value: '3d', label: '3 day' },
  { value: '4d', label: '4 day' },
  { value: '5d', label: '5 day' },
  { value: '6d', label: '6 day' },
  { value: '7d', label: '7 days' },
];

export const SPA_OXYBAR_DURATION_VALUES = SPA_OXYBAR_DURATION_OPTIONS.map((o) => o.value);

/** Duration options for renewal and ancillary sales. */
export const SALE_DURATION_OPTIONS = [
  { value: '1d', label: '1 day' },
  { value: '1m', label: '1 month' },
  { value: '2m', label: '2 months' },
  { value: '3m', label: '3 months' },
  { value: '6m', label: '6 months' },
  { value: '12m', label: '12 months' },
];

/** Subscription page: month durations only. */
export const SUBSCRIPTION_DURATION_OPTIONS = [
  { value: 1, label: '1 month' },
  { value: 2, label: '2 months' },
  { value: 3, label: '3 months' },
  { value: 4, label: '4 months' },
  { value: 5, label: '5 months' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
];

/** Memberships table duration filter: TBC + subscription month labels. */
export const MEMBERSHIP_DURATION_FILTER_OPTIONS = [
  'TBC',
  ...SUBSCRIPTION_DURATION_OPTIONS.map((o) => o.label),
];

export const SALE_DURATION_VALUES = SALE_DURATION_OPTIONS.map((o) => o.value);

export function durationOptionsForSaleSource(source) {
  const src = String(source || '').toLowerCase().replace(/-/g, '_');
  if (src === 'spa_sale' || src === 'oxybar_sale' || src === 'spa' || src === 'oxybar') {
    return SPA_OXYBAR_DURATION_OPTIONS;
  }
  if (src === 'guest_pass') return GUEST_PASS_DURATION_OPTIONS;
  return SALE_DURATION_OPTIONS;
}

export function durationValuesForSaleSource(source) {
  return durationOptionsForSaleSource(source).map((o) => o.value);
}

/** Label for a subscription month count (matches Sales duration dropdown). */
export function subscriptionDurationLabel(months) {
  const n = Number(months);
  const found = SUBSCRIPTION_DURATION_OPTIONS.find((o) => o.value === n);
  if (found) return found.label;
  if (!Number.isFinite(n) || n <= 0) return '';
  return n === 1 ? '1 month' : `${Math.floor(n)} months`;
}

const DURATION_MONTH_ALIASES = {
  monthly: 1,
  month: 1,
  '1m': 1,
  '2m': 2,
  '3m': 3,
  '4m': 4,
  '5m': 5,
  '6m': 6,
  '12m': 12,
  quarterly: 3,
  yearly: 12,
  annual: 12,
  annually: 12,
  year: 12,
};

/**
 * Normalize any stored / catalog duration to Sales option labels
 * (e.g. "Monthly" → "1 month") or "TBC" / "Lifetime".
 */
export function normalizeSubscriptionDurationLabel(raw) {
  const text = String(raw || '').trim();
  if (!text) return '';
  if (/^tbc$/i.test(text)) return 'TBC';
  if (/^lifetime$/i.test(text)) return 'Lifetime';

  const lower = text.toLowerCase().replace(/\s+/g, ' ');
  const byLabel = SUBSCRIPTION_DURATION_OPTIONS.find((o) => o.label.toLowerCase() === lower);
  if (byLabel) return byLabel.label;

  if (Object.prototype.hasOwnProperty.call(DURATION_MONTH_ALIASES, lower)) {
    return subscriptionDurationLabel(DURATION_MONTH_ALIASES[lower]);
  }

  const monthMatch = lower.match(/^(\d+)\s*months?$/);
  if (monthMatch) return subscriptionDurationLabel(Number(monthMatch[1]));

  const dayMatch = lower.match(/^(\d+)\s*days?$/);
  if (dayMatch) {
    const days = Number(dayMatch[1]);
    return days === 1 ? '1 day' : `${days} days`;
  }

  const months = Number((text.match(/(\d+)/) || [])[1]);
  if (Number.isFinite(months) && months > 0) {
    if (/\bday/i.test(text) && !/\bmonth/i.test(text)) {
      return months === 1 ? '1 day' : `${months} days`;
    }
    return subscriptionDurationLabel(months);
  }

  return text;
}

export function parseDurationKey(durationKey) {
  const key = String(durationKey || '1m').trim();
  const dayMatch = key.match(/^(\d+)d$/i);
  if (dayMatch) return { days: Number(dayMatch[1]), months: 0 };
  const monthMatch = key.match(/^(\d+)m$/i);
  return { days: 0, months: monthMatch ? Number(monthMatch[1]) : 1 };
}

export function isValidIsoDate(dateStr) {
  const base = String(dateStr || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(base)) return false;
  const date = new Date(`${base}T12:00:00`);
  return !Number.isNaN(date.getTime());
}

function normalizeIsoDate(dateStr) {
  return isValidIsoDate(dateStr) ? String(dateStr).slice(0, 10) : '';
}

const LEGACY_GUEST_PASS_DURATION = {
  'Day Pass': '1d',
  'Trial Pass': '1d',
  'Week Pass': '7d',
  'Monthly Guest Pass': '30d',
};

export function formatDurationLabel(durationKey, options = SALE_DURATION_OPTIONS) {
  const opts = options || SALE_DURATION_OPTIONS;
  const found = opts.find((o) => o.value === durationKey);
  if (found) return found.label;
  const raw = String(durationKey || '').trim();
  const byLabel = opts.find((o) => String(o.label).toLowerCase() === raw.toLowerCase());
  if (byLabel) return byLabel.label;
  const spa = SPA_OXYBAR_DURATION_OPTIONS.find((o) => o.value === durationKey);
  if (spa) return spa.label;
  const legacy = LEGACY_GUEST_PASS_DURATION[durationKey];
  if (legacy) return formatDurationLabel(legacy, GUEST_PASS_DURATION_OPTIONS);
  return String(durationKey || '—');
}

/** Map a stored label ("3 months") or key ("3m") back to a duration key. */
export function durationKeyFromLabel(raw, options = SALE_DURATION_OPTIONS) {
  const text = String(raw || '').trim();
  if (!text || text === '—') return '';
  const opts = options || SALE_DURATION_OPTIONS;
  if (opts.some((o) => String(o.value) === text)) return text;
  const byLabel = opts.find((o) => String(o.label).toLowerCase() === text.toLowerCase());
  if (byLabel) return byLabel.value;
  const months = text.match(/^(\d+)\s*months?$/i);
  if (months) return `${Number(months[1])}m`;
  const days = text.match(/^(\d+)\s*days?$/i);
  if (days) return `${Number(days[1])}d`;
  if (/^\d+[dm]$/i.test(text)) return text.toLowerCase();
  return '';
}

export function guestPassDurationKey(record) {
  const raw = record?.duration || record?.passType || '';
  if (GUEST_PASS_DURATION_VALUES.includes(raw)) return raw;
  return LEGACY_GUEST_PASS_DURATION[raw] || raw;
}

export function guestPassDurationLabel(record) {
  const key = guestPassDurationKey(record);
  if (GUEST_PASS_DURATION_VALUES.includes(key)) {
    return formatDurationLabel(key, GUEST_PASS_DURATION_OPTIONS);
  }
  const raw = String(record?.duration || record?.passType || '').trim();
  if (raw) {
    const byLabel = GUEST_PASS_DURATION_OPTIONS.find(
      (o) => o.label.toLowerCase() === raw.toLowerCase(),
    );
    if (byLabel) return byLabel.label;
    if (/^\d+(\.\d+)?\s*(day|days)$/i.test(raw)) return raw;
  }
  const legacyLabel = String(record?.passType || '').trim();
  return legacyLabel || '—';
}

export function guestPassEndDate(record) {
  const stored = String(record?.endDate || '').slice(0, 10);
  if (stored) return stored;
  const key = guestPassDurationKey(record);
  const start = String(record?.startDate || '').slice(0, 10);
  if (start && /^\d+d$/.test(key)) return saleEndDate(start, key);
  return '';
}

export function emptyGuestPassPeriodFields(today = '') {
  const date = today || new Date().toISOString().slice(0, 10);
  const duration = '1d';
  return {
    duration,
    startDate: date,
    endDate: saleEndDate(date, duration),
  };
}

/** Keep guest pass start / end in sync when duration or start date changes. */
export function syncGuestPassPeriodFields(prev, patch) {
  const next = { ...prev, ...patch };
  const startDate = String(next.startDate || prev.startDate || '').slice(0, 10);
  const duration = next.duration || prev.duration || '1d';
  if (startDate) {
    next.startDate = startDate;
    next.duration = duration;
    next.endDate = saleEndDate(startDate, duration);
  }
  return next;
}

export function dayAfterDate(dateStr) {
  const base = normalizeIsoDate(dateStr);
  if (!base) return '';
  const date = new Date(`${base}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
}

export function addDurationToDate(dateStr, durationKey) {
  const base = normalizeIsoDate(dateStr);
  if (!base) return '';
  const key = String(durationKey || '').trim();
  if (!/^\d+[dm]$/i.test(key)) return '';
  const date = new Date(`${base}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  const { days, months } = parseDurationKey(key);
  if (days) date.setDate(date.getDate() + days);
  if (months) date.setMonth(date.getMonth() + months);
  // Period ends the day before the next cycle (e.g. start 27/07 → end 26/08 for 1 month)
  date.setDate(date.getDate() - 1);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

/** Inverse of addDurationToDate — recover start from a stored period/membership end. */
export function periodStartFromEndDate(endDateStr, durationKey) {
  const end = normalizeIsoDate(endDateStr);
  const key = String(durationKey || '').trim();
  if (!end || !/^\d+[dm]$/i.test(key)) return '';
  const date = new Date(`${end}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + 1);
  const { days, months } = parseDurationKey(key);
  if (days) date.setDate(date.getDate() - days);
  if (months) date.setMonth(date.getMonth() - months);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

/** Subscription package duration label → end date (months). */
export function addMonthsToEndDate(dateStr, months) {
  const n = Number(months);
  if (!Number.isFinite(n) || n <= 0) return '';
  return addDurationToDate(dateStr, `${Math.floor(n)}m`);
}

export function formatDisplayDate(dateStr) {
  if (!dateStr) return '—';
  const [year, month, day] = String(dateStr).slice(0, 10).split('-');
  if (!year || !month || !day) return '—';
  return `${month}/${day}/${year}`;
}

export function saleEndDate(startDate, durationKey) {
  return addDurationToDate(startDate, durationKey);
}

/** Keep end date in sync when duration or start date changes (renewal / ancillary sales). */
export function syncSalePeriodFields(prev, patch) {
  const next = { ...prev, ...patch };
  const startDate = String(next.startDate || prev.startDate || '').slice(0, 10);
  const duration = next.duration || prev.duration || '1m';
  if (startDate) {
    next.startDate = startDate;
    next.endDate = saleEndDate(startDate, duration);
  }
  return next;
}

export function emptySalePeriodFields(today = '', durationKey = '1m') {
  const date = today || new Date().toISOString().slice(0, 10);
  const duration = durationKey || '1m';
  return {
    duration,
    startDate: date,
    endDate: saleEndDate(date, duration),
  };
}

/** Labels that may be appended as `Item — 1 month` on package/item fields. */
const KNOWN_DURATION_LABELS = [
  ...GUEST_PASS_DURATION_OPTIONS.map((o) => o.label),
  ...SPA_OXYBAR_DURATION_OPTIONS.map((o) => o.label),
  ...SALE_DURATION_OPTIONS.map((o) => o.label),
  ...SUBSCRIPTION_DURATION_OPTIONS.map((o) => o.label),
];

function looksLikeDurationLabel(text) {
  const value = String(text || '').trim();
  if (!value) return false;
  const lower = value.toLowerCase();
  if (KNOWN_DURATION_LABELS.some((label) => label.toLowerCase() === lower)) return true;
  if (/^tbc$/i.test(value) || /^lifetime$/i.test(value)) return true;
  if (/^\d+\s*(day|days|month|months)$/i.test(value)) return true;
  if (/^\d+[md]$/i.test(value)) return true;
  if (/^\d+(\.\d+)?$/.test(value)) return true;
  return false;
}

/**
 * Split stored package/item text that may include ` — duration`.
 * Returns clean package name + duration label (either may be empty).
 */
export function splitSalePackageAndDuration(packageOrItem) {
  const text = String(packageOrItem || '').trim();
  if (!text || text === '—') return { packageName: '', durationLabel: '' };

  const match = text.match(/^(.*?)\s*[—\-]\s*(.+)$/);
  if (match) {
    const left = match[1].trim();
    const right = match[2].trim();
    if (looksLikeDurationLabel(right)) {
      return { packageName: left, durationLabel: right };
    }
    // Nested: "PT Sale — Sessions — 1 month"
    const nested = splitSalePackageAndDuration(right);
    if (nested.durationLabel) {
      const packageName = [left, nested.packageName].filter(Boolean).join(' — ');
      return { packageName, durationLabel: nested.durationLabel };
    }
  }

  if (looksLikeDurationLabel(text)) {
    return { packageName: '', durationLabel: text };
  }

  return { packageName: text, durationLabel: '' };
}

/** Package / item name without trailing duration. */
export function cleanSalePackageName(packageOrItem, fallback = '—') {
  const { packageName, durationLabel } = splitSalePackageAndDuration(packageOrItem);
  if (packageName) return packageName;
  if (durationLabel) return fallback;
  return String(packageOrItem || '').trim() || fallback;
}

/** Duration extracted from a package/item string, if present. */
export function durationFromSalePackage(packageOrItem) {
  return splitSalePackageAndDuration(packageOrItem).durationLabel || '';
}
